import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { saveAs } from 'file-saver';
import { Subscription } from 'rxjs';
import { AttachmentService } from './attachment-service/attachment.service';
import { Attachment } from './attachment-service/models/attachment-model';

@Component({
  selector: 'app-attachments',
  templateUrl: './attachments.component.html',
  styleUrls: ['./attachments.component.scss']
})
export class AttachmentsComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();

  @Input() supportedFileTypes: Array<string>;
  @Input() maxFileSize: number;
  @Input() maxCumulativeFileSize: number;
  @Input() saveRoute: string;
  @Input() isRequired: boolean;
  @Input() attachments: Array<Attachment>;
  @Input() downloadOnly = false;
  @Input() downloadRoute: string;

  acceptTypes: string;
  acceptTypesFriendlyFormat: string;
  sizeLimitFriendlyFormat: string;
  showError: boolean;

  constructor(private attachmentService: AttachmentService) { }

  ngOnInit() {
    if (this.attachments === undefined) {
      this.attachments = new Array<Attachment>();
      this.attachments.push(new Attachment());
    }

    if (!this.downloadOnly) {
      this.modifyExistingAttachmentStatus();
      this.createAcceptTypes();
      this.createFriendlySizeLimit();
    }
  }

  modifyExistingAttachmentStatus() {
    this.attachments.forEach(x => {
      if (x.name !== undefined && x.name !== null && x.name !== '') {
        x.uploadSuccessful = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  createAcceptTypes() {
    this.acceptTypes = this.supportedFileTypes.join();
    this.acceptTypesFriendlyFormat = this.supportedFileTypes.join(', ').toUpperCase();
  }

  createFriendlySizeLimit() {
    this.sizeLimitFriendlyFormat = this.turnBytesToMegaBytes(this.maxFileSize).toString();
  }

  turnBytesToMegaBytes(bytes: number): number {
    return bytes / 1048576;
  }

  ensureCanReplaceSameDocument($event) {
    $event.target.value = null;
  }

  upload($event) {
    this.showError = false;
    const attachment = new Attachment();

    attachment.file = null;
    attachment.name = null;
    attachment.uploadSuccessful = false;

    const fileTypeNotSupported = !this.isValidFileType($event.srcElement.files[0].name);
    const fileIsTooBig = !this.isValidFileSize($event.srcElement.files[0].size);
    const cumulativeSizeIsTooBig = !this.isValidCumulativeFileSize($event.srcElement.files[0].size);

    if (fileTypeNotSupported || fileIsTooBig || cumulativeSizeIsTooBig) {
      attachment.uploadInvalid = true;
      this.showError = true;
      return;
    }

    attachment.uploadInvalid = false;
    attachment.file = $event.target.files[0];
    attachment.name = attachment.file.name;
    attachment.size = $event.srcElement.files[0].size;

    attachment.uploading = true;
    attachment.uploadSuccessful = false;

    this.subscription.add(this.attachmentService.uploadFile(this.saveRoute, 0, attachment.file).subscribe(
      data => {
        attachment.location = data.headers.get('location');
        attachment.uploading = false;
        attachment.uploadSuccessful = true;
        this.attachments.push(attachment);
      },
      error => {
        attachment.uploading = false;
        attachment.uploadSuccessful = true;
        this.showError = true;
      }
    ));
  }

  removeAttachment(attachment: Attachment) {
    const index = this.attachments.indexOf(attachment);
    if (index > -1) {
      this.attachments.splice(index, 1);
    }
  }

  downloadAttachment(attachment: Attachment) {
    if (this.downloadOnly && this.downloadRoute) {
      this.subscription.add(
        this.attachmentService.downloadAttachment(this.downloadRoute, attachment)
        .subscribe(response => {
          saveAs(new Blob([response]), attachment.name);
        }));
    } else {
      saveAs(new Blob([attachment.file]), attachment.name);
    }
  }

  hideAlert() {
    this.showError = false;
  }

  private isValidFileSize(size: number): boolean {
    return this.maxFileSize >= size;
  }

  private isValidCumulativeFileSize(size: number): boolean {
    let cumulativeFileSize = size;

    this.attachments.forEach(x => {
      cumulativeFileSize += x.size;
    });

    return this.maxCumulativeFileSize >= cumulativeFileSize;
  }

  private isValidFileType(filename: string): boolean {
    const fileExtension = filename.substring(filename.lastIndexOf('.')).toLocaleLowerCase();

    return this.supportedFileTypes.some(x => x === fileExtension);
  }
}
